import { z } from 'zod';
import { BaseRequestSchema, LabelRefSchema, ProviderMessageIdSchema } from './primitives.ts';

export const ListMessagesSchema = BaseRequestSchema.extend({
  action: z.literal('list_messages'),
  pageToken: z.string().optional(),
  maxResults: z.number().int().min(1).max(100).default(25),
  labelIds: z.array(LabelRefSchema).optional(),
  query: z.string().max(1024).optional(),
  /**
   * Provider-specific folder hint. Outlook's `/me/messages` returns all
   * folders by default; callers that want just Inbox / Archive / Sent /
   * Deleted / Junk pass this. Gmail's listing uses labels (`labelIds`)
   * for the same purpose and ignores `folder`. Bounded enum to prevent
   * open-ended mailbox-path injection — the legacy Outlook handler's
   * `folderMap` enumerates exactly these values.
   */
  folder: z.enum(['inbox', 'archive', 'sent', 'deleted', 'spam']).optional(),
  /**
   * ISO-8601 datetime, with or without a timezone offset. When set,
   * providers filter to messages received at or after this timestamp.
   * - Outlook: emitted as `$filter=receivedDateTime ge <iso>`. Combines
   *   with `labelIds` via OData `and` (parenthesised composition). Graph
   *   accepts both `Z` and `±HH:MM` offsets.
   * - Gmail: emitted as `q="after:YYYY/MM/DD"`. Gmail's `q` is locale-
   *   naive and date-only — the adapter subtracts 1 day before formatting
   *   to guarantee overshoot in every timezone (Gmail interprets the
   *   `YYYY/MM/DD` as midnight in the mailbox owner's TZ). Idempotent
   *   upsert absorbs duplicates.
   */
  afterDate: z.string().datetime({ offset: true }).optional(),
});

export const SearchMessagesSchema = BaseRequestSchema.extend({
  action: z.literal('search_messages'),
  query: z.string().min(1).max(1024),
  maxResults: z.number().int().min(1).max(100).default(25),
  pageToken: z.string().optional(),
});

export const GetMessageSchema = BaseRequestSchema.extend({
  action: z.literal('get_message'),
  messageId: ProviderMessageIdSchema,
  format: z.enum(['full', 'metadata', 'raw']).default('full'),
});

export const GetThreadSchema = BaseRequestSchema.extend({
  action: z.literal('get_thread'),
  threadId: ProviderMessageIdSchema,
});

export const GetAttachmentSchema = BaseRequestSchema.extend({
  action: z.literal('get_attachment'),
  messageId: ProviderMessageIdSchema,
  attachmentId: z.string().min(1).max(1024),
});

export const ListLabelsSchema = BaseRequestSchema.extend({
  action: z.literal('list_labels'),
});

export const GetProfileSchema = BaseRequestSchema.extend({
  action: z.literal('get_profile'),
});

export const GetProfilePhotoSchema = BaseRequestSchema.extend({
  action: z.literal('get_profile_photo'),
  // `size` deliberately omitted — Google People API returns a single URL
  // that callers resize client-side (appending `=sXXX-c`), and Microsoft
  // Graph's `/photo/$value` endpoint doesn't accept a size query. Adding
  // the field to the contract before a real call-site needs it invites
  // drift between schema and adapter behavior.
});

export const GetProfileNameSchema = BaseRequestSchema.extend({
  action: z.literal('get_profile_name'),
});

/**
 * On-demand body fetch + persist. Called by the frontend when the per-thread
 * fast-poll window expires without `email_bodies.body_html` landing (see
 * `frontend/web/src/hooks/use-email-thread-db.ts`). The dispatcher resolves the
 * `email_metadata` row, refreshes the provider token, calls the adapter's
 * `getMessage`, and upserts `email_bodies` through the same shared
 * `store-bodies` module the sync + backfill paths use.
 *
 * Unlike other read actions this does NOT take `accountId`: the row's owning
 * account is derived from `email_metadata.account_id` after the org-scoped
 * lookup. `accountId` is explicitly omitted from the schema (mirroring
 * `GetCapabilitiesSchema`) so a caller cannot smuggle a sibling account id
 * on the wire and have it inadvertently honoured by downstream code.
 */
export const FetchMessageBodySchema = BaseRequestSchema.extend({
  action: z.literal('fetch_message_body'),
  emailMetadataId: z.string().uuid(),
}).omit({ accountId: true });

/**
 * On-demand fetch of the full original message source (RFC 822 / MIME) for a
 * single email, for the "View raw email" affordance. Read-only: unlike
 * `fetch_message_body` it persists nothing — the source is returned inline and
 * never stored (it carries the same PII as the rendered body and has no reuse
 * value beyond the open dialog).
 *
 * Like `fetch_message_body`, the owning account is derived server-side from
 * `email_metadata.account_id` after an org-scoped lookup, so `accountId` is
 * omitted from the wire schema — a caller cannot smuggle a sibling account id.
 */
export const FetchRawMessageSchema = BaseRequestSchema.extend({
  action: z.literal('fetch_raw_message'),
  emailMetadataId: z.string().uuid(),
}).omit({ accountId: true });
